if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // Hide quantity selector and buy buttons and sticky-add-to-cart if price is hidden.
        // sectionId is Dawn's and returns the ORIGINAL section id, because it builds Section
        // Rendering URLs. quick-add.js rewrites every id in an injected fragment to `quickadd-*`,
        // so a DOM lookup must use dataset.section. On a PDP the two are the same; in the panel
        // they differ, and an unmatched lookup reads as "no price" and hides the buy buttons.
        const hidePriceBlock =
          this.querySelector(`#price-${this.dataset.section}`) || this.querySelector(`#price-${this.sectionId}`);
        const isPriceHidden = !hidePriceBlock || hidePriceBlock.querySelector('.price') === null;
        this.toggleBuyButtonsVisibility(isPriceHidden);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        // `url` may already carry a query string (e.g. "?view=cp-panel").
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const updateSourceFromDestinationWithClasses = (cssClass) => {
            const source = html.querySelector(`.${cssClass}-${this.sectionId}`);
            const destinations = this.querySelectorAll(`.${cssClass}-${this.dataset.section}`);

            if (!source || destinations.length === 0) return;
            const htmlContent = source.innerHTML;
            destinations.forEach((el) => {
              el.innerHTML = htmlContent;
            });
          };

          const syncClasses = [
            'product__weight',
            'product__sku',
            'product__price',
            'product__image',
            'availability__value',
            'demo__value',
            'dimensions__value',
            'product__specs',
          ];

          syncClasses.forEach(updateSourceFromDestinationWithClasses);

          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // --- NEW: mettre à jour le bloc quantité complet ---
          const quantitySource = html.getElementById(`Quantity-Form-${this.sectionId}`);
          const quantityDestination = this.querySelector(`#Quantity-Form-${this.dataset.section}`);
          if (quantitySource && quantityDestination) {
            quantityDestination.innerHTML = quantitySource.innerHTML;
          }
          // ----------------------------------------------------

          // Hide quantity selector and buy buttons and sticky-add-to-cart if price is hidden
          const hidePriceBlock = html.getElementById(`price-${this.sectionId}`);
          const isPriceHidden = !hidePriceBlock || hidePriceBlock.querySelector('.price') === null;
          this.toggleBuyButtonsVisibility(isPriceHidden);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const mediaThumbnailsSource = this.querySelector('.thumbnail-list');
        const mediaThumbnailsDestination = html.querySelector('.thumbnail-list');

        const refreshSourceData = (sourceItems) => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaSourceItems = Array.from(sourceItems.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaSourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(mediaSourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
          return [mediaSourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          this.updateMediaItems(mediaGallerySource, mediaGalleryDestination, refreshSourceData);
        }

        if (mediaThumbnailsSource && mediaThumbnailsDestination) {
          this.updateMediaItems(mediaThumbnailsSource, mediaThumbnailsDestination, refreshSourceData);
        }

        // Bind click on new thumbnails
        if (mediaThumbnailsSource) {
          this.querySelector(`media-gallery`)?.handleThumbnailsClick?.(
            mediaThumbnailsSource,
            '[data-variant-thumbnail]'
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateMediaItems(sourceItems, mediaDestination, refreshSourceData) {
        let [mediaSourceItems, sourceSet, sourceMap] = refreshSourceData(sourceItems);
        const mediaDestinationItems = Array.from(mediaDestination.querySelectorAll('li[data-media-id]'));
        const destinationSet = new Set(mediaDestinationItems.map(({ dataset }) => dataset.mediaId));
        let shouldRefresh = false;

        // add items from new data not present in DOM
        for (let i = mediaDestinationItems.length - 1; i >= 0; i--) {
          if (!sourceSet.has(mediaDestinationItems[i].dataset.mediaId)) {
            sourceItems.prepend(mediaDestinationItems[i]);
            shouldRefresh = true;
          }
        }

        // remove items from DOM not present in new data
        for (let i = 0; i < mediaSourceItems.length; i++) {
          if (!destinationSet.has(mediaSourceItems[i].dataset.mediaId)) {
            mediaSourceItems[i].remove();
            shouldRefresh = true;
          }
        }

        // refresh
        if (shouldRefresh) [mediaSourceItems, sourceSet, sourceMap] = refreshSourceData(sourceItems);

        // if media galleries don't match, sort to match new data order
        mediaDestinationItems.forEach((destinationItem, destinationIndex) => {
          const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

          if (sourceData && sourceData.index !== destinationIndex) {
            sourceItems.insertBefore(
              sourceData.item,
              sourceItems.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
            );

            // refresh source now that it has been modified
            [mediaSourceItems, sourceSet, sourceMap] = refreshSourceData(sourceItems);
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        // Only featured-product.liquid renders .quantity__rules-cart, so this is null on the PDP
        // and in the complementary panel - unguarded it threw on every cartUpdate.
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      toggleBuyButtonsVisibility(isPriceHidden) {
        const quantityForm = this.querySelector(`#Quantity-Form-${this.dataset.section}`);
        const buyButtons = this.querySelector('.product-buy-buttons');
        const stickyAddToCart = this.querySelector('#stickyActionToCart');

        if (quantityForm) {
          quantityForm.style.display = isPriceHidden ? 'none' : '';
        }
        if (buyButtons) {
          buyButtons.style.display = isPriceHidden ? 'none' : '';
        }
        if (stickyAddToCart) {
          stickyAddToCart.style.display = isPriceHidden ? 'none' : '';
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

/* Modification GMS 07/10/26 */
/*
document.addEventListener('DOMContentLoaded', () => {
  const closeOptions = document.querySelectorAll('.offcanvas-close-button, .menu-drawer-overlay');
  const offcanvasElements = document.querySelectorAll(
    '.product__info-specs-wrapper details, .product__info-appointment details'
  );

  closeOptions?.forEach((closeOption) => {
    closeOption.addEventListener('click', () => {
      offcanvasElements.forEach((offcanvas) => {
        offcanvas.removeAttribute('open');
      });
    });
  });

  offcanvasElements.forEach((detail) => {
    detail.addEventListener('toggle', () => {
      if (detail.open) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    });
  });
});
*/

document.addEventListener('click', (event) => {
  const closeButton = event.target.closest(
    '.offcanvas-close-button, .menu-drawer-overlay'
  );

  if (!closeButton) return;

  const offcanvasElements = document.querySelectorAll(
    '.product__info-specs-wrapper details, .product__info-appointment details'
  );

  offcanvasElements.forEach((offcanvas) => {
    offcanvas.removeAttribute('open');
  });
});

document.addEventListener('toggle', (event) => {
  if (!event.target.matches('.product__info-specs-wrapper details, .product__info-appointment details')) {
    return;
  }

  const isAnyOffcanvasOpen = document.querySelector(
    '.product__info-specs-wrapper details[open], .product__info-appointment details[open]'
  );

  document.body.classList.toggle(
    'overflow-hidden',
    Boolean(isAnyOffcanvasOpen)
  );
}, true);


document.addEventListener('click', function (event) {
  const ratingWrapper = event.target.closest('.rating-wrapper');

  if (!ratingWrapper) return;

  const reviews = document.querySelector('#yotpo-app');

  if (!reviews) return;

  event.preventDefault();

  reviews.scrollIntoView({
    behavior: 'instant',
    block: 'start'
  });
});